import { useEffect, useRef, useState } from "react";
import { supabase } from "./supabase.js";

/* Two people in the same contract at the same time.
 *
 * Postgres publishes every change to the tables in 0013, and Row Level
 * Security decides who is allowed to hear each one, so a member assigned to
 * VIDE is never sent a WAPA row. Nothing here can widen what anybody can see;
 * it only changes when they see it.
 *
 * Everything in this file is a no-op when Supabase is not configured, which is
 * how the visual preview renders these screens with no database behind them.
 */

/* Channel names have to be unique per subscription. Two panels watching the
   same table would otherwise share one channel and unsubscribe each other. */
let n = 0;
const uniqueName = (prefix) => `${prefix}#${(n += 1)}`;

/* Tell me when a row changes.
 *
 * `filter` is Postgres-side, in Supabase's own syntax, so the server does the
 * narrowing: contract_id=eq.<uuid>. Without it every client would be woken by
 * every change in the project and would have to sort it out itself.
 */
export function useLiveRows(table, filter, onChange, enabled = true) {
  /* The handler is held in a ref so a caller can pass a fresh closure on every
     render without tearing the subscription down and building it again. */
  const handler = useRef(onChange);
  useEffect(() => { handler.current = onChange; }, [onChange]);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!supabase || !enabled || !table || !filter) { setLive(false); return undefined; }

    const channel = supabase
      .channel(uniqueName(`rows:${table}`))
      .on("postgres_changes", { event: "*", schema: "public", table, filter },
        (payload) => handler.current?.(payload))
      .subscribe((status) => setLive(status === "SUBSCRIBED"));

    return () => { setLive(false); supabase.removeChannel(channel); };
  }, [table, filter, enabled]);

  return live;
}

/* When the socket cannot be had.
 *
 * Some networks block WebSockets outright, and a connection on an island drops
 * for reasons nobody controls. The failure mode that matters is not the missing
 * feature, it is not knowing: two people would go on reading different numbers
 * off the same register believing they were in sync. So when the channel is not
 * live the screen falls back to asking again on a timer, which is slower than
 * live and much better than stale, and the top bar says which of the two is
 * happening.
 */
export function useFallbackPoll(refresh, live, seconds = 30) {
  const fn = useRef(refresh);
  useEffect(() => { fn.current = refresh; }, [refresh]);

  useEffect(() => {
    if (live || !supabase) return undefined;
    const t = setInterval(() => fn.current?.(), seconds * 1000);
    return () => clearInterval(t);
  }, [live, seconds]);
}

/* The same thing for a page that reads several registers at once.
 *
 * One channel with a listener per table rather than a hook per table, because
 * the number of tables a page watches is a property of the page and React
 * will not have a hook appear and disappear between renders.
 */
export function useLiveTables(tables, filter, onChange, enabled = true) {
  const handler = useRef(onChange);
  useEffect(() => { handler.current = onChange; }, [onChange]);
  const [live, setLive] = useState(false);

  const key = [...tables].sort().join(",");

  useEffect(() => {
    if (!supabase || !enabled || !key || !filter) { setLive(false); return undefined; }

    const channel = supabase.channel(uniqueName("rows:many"));
    for (const table of key.split(",")) {
      channel.on("postgres_changes", { event: "*", schema: "public", table, filter },
        (payload) => handler.current?.(payload));
    }
    channel.subscribe((status) => setLive(status === "SUBSCRIBED"));

    return () => { setLive(false); supabase.removeChannel(channel); };
  }, [key, filter, enabled]);

  return live;
}

/* Who else is in here.
 *
 * Presence is a broadcast between browsers rather than anything in the
 * database, so it needs no table and leaves no trace. It answers the question
 * that actually gets asked on a call, which is whether the person you are
 * talking to is looking at the same screen you are.
 */
export function usePresence(room, me) {
  const [others, setOthers] = useState([]);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!supabase || !room || !me?.id) { setOthers([]); setLive(false); return undefined; }

    const channel = supabase.channel(`here:${room}`, {
      config: { presence: { key: me.id } },
    });

    const sync = () => {
      const state = channel.presenceState();
      setOthers(Object.entries(state)
        /* Everybody but me. Two tabs of my own are still me. */
        .filter(([id]) => id !== me.id)
        .map(([id, entries]) => ({ id, name: entries?.[0]?.name ?? "Someone" })));
    };

    channel
      .on("presence", { event: "sync" }, sync)
      .subscribe((status) => {
        setLive(status === "SUBSCRIBED");
        if (status === "SUBSCRIBED") channel.track({ name: me.name ?? "Someone" });
      });

    return () => { setLive(false); setOthers([]); supabase.removeChannel(channel); };
  }, [room, me?.id, me?.name]);

  /* Presence rides the same socket as the change feed, so whether it is
     connected is the honest answer to "is this screen live". */
  return { others, live };
}
